<template>
  <div class="pending-users">
    <p class="pending-users__hint">
      Employees who signed in with Microsoft but have no company or role yet.
      Assign one so they can use the panel.
    </p>
    <DataTable :value="users" :loading="isLoading" responsiveLayout="scroll">
      <template #empty>No pending users.</template>
      <Column field="name" header="Name" />
      <Column field="email" header="Email" />
      <Column field="created_at" header="First sign in">
        <template #body="{ data }">{{ formatDate(data.created_at) }}</template>
      </Column>
      <Column header="">
        <template #body="{ data }">
          <b-button class="pending-users__button" @click="openAssign(data)">Assign</b-button>
        </template>
      </Column>
    </DataTable>

    <b-modal
      hide-footer
      ref="assign-modal"
      size="lg"
      :title="selected ? `Assign ${selected.email}` : 'Assign'"
    >
      <div class="pending-users__row">
        <div class="pending-users__column">
          <label for="assign-role">Role <b style="color: red">*</b></label>
          <Dropdown
            id="assign-role"
            v-model="assignment.role"
            :options="roles"
            optionLabel="name"
            optionValue="code"
            @change="roleChanged"
          />
        </div>
        <div class="pending-users__column" v-if="assignment.role !== 'global_admin'">
          <label for="assign-company">Company <b style="color: red">*</b></label>
          <Dropdown
            id="assign-company"
            v-model="assignment.company_id"
            :options="companies"
            optionLabel="name"
            optionValue="id"
            @change="loadDepartments"
          />
        </div>
        <div class="pending-users__column" v-if="assignment.role === 'department_admin'">
          <label for="assign-departments">Department/s <b style="color: red">*</b></label>
          <MultiSelect
            id="assign-departments"
            v-model="assignment.department_ids"
            :options="departments"
            optionLabel="department_name"
            optionValue="department_id"
            placeholder=""
          />
        </div>
      </div>
      <b-overlay :show="isSaving" rounded opacity="0.6" spinner-small>
        <b-button class="pending-users__button" :disabled="!canSave" @click="save()">
          Save
        </b-button>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import { successMessage } from "@/utils/alertMessages";

export default {
  name: "PendingUsers",
  data() {
    return {
      users: [],
      companies: [],
      departments: [],
      selected: null,
      assignment: { role: "", company_id: null, department_ids: [] },
      isLoading: false,
      isSaving: false,
      roles: [
        { name: "Global Admin", code: "global_admin" },
        { name: "Company Admin", code: "company_admin" },
        { name: "Department Admin", code: "department_admin" },
      ],
    };
  },
  computed: {
    canSave() {
      const { role, company_id: companyId, department_ids: departmentIds } = this.assignment;
      if (role === "global_admin") return true;
      if (role === "company_admin") return Boolean(companyId);
      if (role === "department_admin") return Boolean(companyId) && departmentIds.length > 0;
      return false;
    },
  },
  created() {
    this.getPendingUsers();
  },
  methods: {
    async getPendingUsers() {
      this.isLoading = true;
      this.users = await this.$store.dispatch("user/getPendingUsers", { skip: 0, limit: 100 });
      this.isLoading = false;
    },
    async openAssign(user) {
      this.selected = user;
      this.assignment = { role: "", company_id: null, department_ids: [] };
      this.departments = [];
      if (this.companies.length === 0) {
        const companiesData = await this.$store.dispatch("company/getAllCompanies", {
          skip: 0,
          limit: 100,
        });
        this.companies = companiesData?.data || [];
      }
      this.$refs["assign-modal"].show();
    },
    roleChanged() {
      if (this.assignment.role === "global_admin") {
        this.assignment.company_id = null;
      }
      this.assignment.department_ids = [];
    },
    async loadDepartments() {
      this.assignment.department_ids = [];
      const depData = await this.$store.dispatch("company/getDepartmentsPerCompany", {
        company_id: this.assignment.company_id,
        department_id: -2,
      });
      this.departments = depData?.data || [];
    },
    async save() {
      this.isSaving = true;
      const { role, company_id: companyId, department_ids: departmentIds } = this.assignment;
      const assignment = {
        role,
        company_id: role === "global_admin" ? null : companyId,
        department_ids: role === "department_admin" ? departmentIds : [],
      };
      const response = await this.$store.dispatch("user/assignUser", {
        userId: this.selected.id,
        assignment,
      });
      this.isSaving = false;
      if (response?.status === 200) {
        this.$refs["assign-modal"].hide();
        successMessage("User assigned. They will get access on their next sign in.");
        this.getPendingUsers();
      }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : "";
    },
  },
};
</script>

<style lang="scss" scoped>
.pending-users {
  &__hint {
    text-align: left;
  }
  &__row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
  }
  &__column {
    display: flex;
    flex-direction: column;
    flex: 1;
    text-align: left;
  }
  &__button {
    @include submit-button;
  }
}
</style>
